import { type BadgeReward } from 'types/api'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useApiClient } from 'hooks/useApiClient'
import { type BadgeRewardRequest } from 'types/fileUpload'

interface usePutBadgeRewardProps {
  id: number
  onSuccess?: (data: BadgeReward, variables: BadgeRewardRequest) => void
  onError?: (error: Error, variables: BadgeRewardRequest) => void
}

export const usePutImageBadgeReward = ({
  id,
  onSuccess,
  onError,
}: usePutBadgeRewardProps) => {
  const putAPIClient = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (badgeReward: BadgeRewardRequest) => {
      const body = new FormData()
      body.set('FileUpload', badgeReward.File)
      body.set('Type', badgeReward.Type)
      body.set('FileName', badgeReward.FileName)
      body.set('Description', badgeReward.Description)
      body.set('Name', badgeReward.Name)
      body.set('ImageNotEarned', badgeReward.ImageNotEarned)
      body.set('ImageEarned', badgeReward.ImageEarned)
      body.set('RenewableType', badgeReward.RenewableType)

      const { data, error } = await putAPIClient.put(
        '/admin/badgerewards/{id}/upload',
        {
          params: { path: { id } },
          body: {
            Name: badgeReward.FileName,
            Type: badgeReward.Type,
            Description: badgeReward.Description,
            FileName: badgeReward.FileName,
            ImageNotEarned: badgeReward.ImageNotEarned,
            ImageEarned: badgeReward.ImageEarned,
            RenewableType: badgeReward.RenewableType,
            Category: badgeReward.Category,
          },
          bodySerializer: () => body,
        }
      )
      return { data, error }
    },
    onSuccess: async ({ data, error }, variables: BadgeRewardRequest) => {
      await queryClient.invalidateQueries({
        queryKey: ['/admin/badgerewards/{id}', id],
      })
      if (error !== undefined) {
        onError?.(error as Error, variables)
      } else {
        onSuccess?.(data as BadgeReward, variables)
      }
    },
    onError,
  })
}
